/**
 * ExecAtoms — shared primitive components for executive (IC) sub-pages.
 *
 * Ported from the v2 Claude Design handoff (`aura-pages.jsx`, `aura-shared.jsx`,
 * `aura-exec.jsx`). Design tokens live in `index.css` (`.aura-root` scope).
 *
 * UI-only. No backend contract. No tenant-specific logic.
 */
import type { CSSProperties, ReactNode } from 'react';

/* ------------------------------------------------------------------ */
/*  Formatting                                                         */
/* ------------------------------------------------------------------ */

export function formatNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-IN');
}

export function compactINR(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '\u2014';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)}K`;
  return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
}

/* ------------------------------------------------------------------ */
/*  Page frame                                                         */
/* ------------------------------------------------------------------ */

interface PageFrameProps {
  title: string;
  sub?: string;
  actions?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}

export function PageFrame({ title, sub, actions, children, dense }: PageFrameProps) {
  return (
    <div
      className="max-sm:p-0!"
      style={{
        padding: dense ? 20 : 24,
        minHeight: '100%',
        background: 'var(--ivory-0)',
      }}
    >
      <div className="max-sm:flex-wrap! max-sm:gap-3" style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          <h2
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 24,
              fontWeight: 500,
              letterSpacing: -0.02,
              marginBottom: 2,
              color: 'var(--ink-0)',
            }}
          >
            {title}
          </h2>
          {sub && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{sub}</div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6 }}>{actions}</div>
      </div>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Toolbar                                                            */
/* ------------------------------------------------------------------ */

export function Toolbar({ left, right }: { left?: ReactNode; right?: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        flexWrap: 'wrap',
      }}
    >
      <div className="max-sm:w-full max-sm:flex-wrap!" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{left}</div>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{right}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Search input                                                       */
/* ------------------------------------------------------------------ */

export function SearchInput({
  placeholder = 'Search…',
  value,
  onChange,
  w = 280,
}: {
  placeholder?: string;
  value?: string;
  onChange?: (v: string) => void;
  w?: number;
}) {
  return (
    <div className="max-sm:w-full!" style={{ position: 'relative', width: w }}>
      <svg
        width={13}
        height={13}
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--ink-3)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="max-sm:top-[13px]!"
        style={{ position: 'absolute', left: 10, top: 8 }}
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        className="input max-sm:h-10!"
        style={{ paddingLeft: 28, width: '100%', height: 30, fontSize: 12 }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tabs                                                               */
/* ------------------------------------------------------------------ */

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div
      className="max-sm:overflow-x-auto max-sm:overflow-y-hidden"
      style={{
        display: 'flex',
        gap: 2,
        borderBottom: '1px solid var(--line)',
        marginBottom: 14,
      }}
    >
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className="max-sm:min-h-[40px] max-sm:shrink-0 max-sm:whitespace-nowrap"
          style={{
            background: 'none',
            border: 'none',
            padding: '8px 12px',
            fontSize: 12,
            cursor: 'pointer',
            color: active === t ? 'var(--ink-0)' : 'var(--ink-3)',
            fontWeight: active === t ? 500 : 400,
            borderBottom:
              active === t
                ? '2px solid var(--ink-0)'
                : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  KPI card                                                           */
/* ------------------------------------------------------------------ */

export function Kpi({
  label,
  value,
  delta,
  color,
}: {
  label: string;
  value: string;
  delta?: string;
  color?: string;
}) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          letterSpacing: 0.06,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontFamily: 'var(--ff-serif)',
          fontWeight: 500,
          letterSpacing: -0.02,
          color: color ?? 'var(--ink-0)',
        }}
      >
        {value}
      </div>
      {delta && (
        <div
          style={{
            fontSize: 11,
            color:
              delta.startsWith('+')
                ? 'var(--success)'
                : delta.startsWith('-')
                  ? 'var(--danger)'
                  : 'var(--ink-3)',
            marginTop: 6,
          }}
        >
          {delta}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Chip                                                               */
/* ------------------------------------------------------------------ */

export function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px',
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 500,
        color,
        background: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Progress bar                                                       */
/* ------------------------------------------------------------------ */

export function Progress({
  value,
  max = 100,
  color = 'var(--ink-0)',
  h = 6,
  bg = 'var(--ivory-3)',
}: {
  value: number;
  max?: number;
  color?: string;
  h?: number;
  bg?: string;
}) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div
      style={{
        width: '100%',
        height: h,
        background: bg,
        borderRadius: h / 2,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          transition: 'width .3s',
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sparkline                                                          */
/* ------------------------------------------------------------------ */

export function Spark({
  values,
  w = 64,
  h = 20,
  color = 'var(--ink-0)',
}: {
  values: number[];
  w?: number;
  h?: number;
  color?: string;
}) {
  if (!values.length) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Avatar                                                             */
/* ------------------------------------------------------------------ */

export function Avatar({
  name,
  size = 28,
  bg,
}: {
  name: string;
  size?: number;
  bg?: string;
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        background: bg ?? 'var(--ivory-2)',
        color: 'var(--ink-1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DataTable                                                          */
/* ------------------------------------------------------------------ */

export interface DataTableColumn {
  k: string;
  label: string;
  w?: string;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  muted?: boolean;
}

export interface DataTableRow {
  [k: string]: unknown;
}

export function DataTable({
  columns,
  rows,
  minRowH = 36,
}: {
  columns: DataTableColumn[];
  rows: DataTableRow[];
  minRowH?: number;
}) {
  const cols = columns.map((c) => c.w || '1fr').join(' ');
  return (
    <div className="card max-sm:overflow-x-auto!" style={{ overflow: 'hidden' }}>
      <div
        className={columns.length > 2 ? 'max-sm:min-w-[560px]' : undefined}
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          // A right-aligned column must not run into the next header
          // ("WONQUOTA" on the Reps table, 2026-10-03).
          columnGap: 16,
          background: 'var(--ivory-1)',
          padding: '9px 12px',
          borderBottom: '1px solid var(--line)',
          fontSize: 11,
          color: 'var(--ink-3)',
          letterSpacing: 0.06,
          textTransform: 'uppercase',
          fontWeight: 500,
        }}
      >
        {columns.map((c, ci) => (
          <div key={c.k} className={ci === 0 && columns.length > 2 ? 'max-sm:sticky max-sm:left-0 max-sm:z-[1] max-sm:-ml-3 max-sm:bg-[var(--ivory-1)] max-sm:pl-3' : undefined} style={{ textAlign: c.align || 'left' }}>
            {c.label}
          </div>
        ))}
      </div>
      {rows.length === 0 && (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            color: 'var(--ink-3)',
            fontSize: 12,
          }}
        >
          No rows
        </div>
      )}
      {rows.map((r, i) => (
        <div
          key={i}
          className={columns.length > 2 ? 'max-sm:min-w-[560px]' : undefined}
          style={{
            display: 'grid',
            gridTemplateColumns: cols,
            columnGap: 16,
            padding: '10px 12px',
            borderBottom:
              i === rows.length - 1 ? 'none' : '1px solid var(--line)',
            fontSize: 13,
            minHeight: minRowH,
            alignItems: 'center',
          }}
        >
          {columns.map((c, ci) => {
            const value = r[c.k];
            const rendered =
              typeof value === 'function'
                ? (value as () => ReactNode)()
                : (value as ReactNode);
            const cellStyle: CSSProperties = {
              textAlign: c.align || 'left',
              color: c.muted ? 'var(--ink-2)' : 'var(--ink-0)',
              fontFamily: c.mono ? 'var(--ff-mono)' : undefined,
              fontSize: c.mono ? 12 : 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            };
            return (
              <div key={c.k} className={ci === 0 && columns.length > 2 ? 'max-sm:sticky max-sm:left-0 max-sm:z-[1] max-sm:-ml-3 max-sm:bg-[var(--ivory-1)] max-sm:pl-3' : undefined} style={cellStyle}>
                {rendered as ReactNode}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  QueueRow                                                           */
/* ------------------------------------------------------------------ */

export function QueueRow({
  leading,
  title,
  meta,
  chips,
  actions,
  accent,
}: {
  leading?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  chips?: ReactNode;
  actions?: ReactNode;
  accent?: string;
}) {
  return (
    <div
      className="card"
      style={{
        padding: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderLeft: `3px solid ${accent || 'transparent'}`,
      }}
    >
      {leading}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-0)' }}>
          {title}
        </div>
        {meta && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              marginTop: 3,
              display: 'flex',
              gap: 4,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {meta}
          </div>
        )}
      </div>
      {chips && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {chips}
        </div>
      )}
      {actions && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>{actions}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  IncentiveBar                                                       */
/* ------------------------------------------------------------------ */

export function IncentiveBar({
  earned,
  target,
  tier,
  nextTier,
  unlock,
}: {
  earned: number;
  target: number;
  tier: string;
  nextTier: string;
  unlock: string;
}) {
  const pct = Math.max(0, Math.min(100, (earned / target) * 100));
  return (
    <div className="card" style={{ padding: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            Current tier
          </div>
          <div
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--saffron-600)',
            }}
          >
            {tier}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            Earned
          </div>
          <div
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--success)',
            }}
          >
            ₹{formatNum(earned)}
          </div>
        </div>
      </div>
      <Progress value={pct} color="var(--saffron)" h={6} />
      <div
        style={{
          display: 'flex',
          marginTop: 8,
          fontSize: 11,
          color: 'var(--ink-3)',
        }}
      >
        <span>{unlock}</span>
        <div style={{ flex: 1 }} />
        <span>Next: {nextTier}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small shared state blocks                                          */
/* ------------------------------------------------------------------ */

export function LoadingBlock() {
  return (
    <div
      className="card"
      style={{
        padding: 40,
        textAlign: 'center',
        color: 'var(--ink-3)',
        fontSize: 12,
      }}
    >
      Loading…
    </div>
  );
}

export function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      className="card"
      style={{
        padding: 40,
        textAlign: 'center',
        color: 'var(--danger)',
        fontSize: 12,
        borderLeft: '3px solid var(--danger)',
      }}
    >
      {message}
    </div>
  );
}

export function EmptyBlock({ message }: { message: string }) {
  return (
    <div
      className="card"
      style={{
        padding: 40,
        textAlign: 'center',
        color: 'var(--ink-3)',
        fontSize: 12,
      }}
    >
      {message}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  useFetch — shared loading/error pattern                            */
/* ------------------------------------------------------------------ */

import { useEffect, useState } from 'react';
import { api } from '@/utils/api';

export type FetchState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

export function useFetch<T>(url: string | null): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: !!url,
    error: null,
  });
  useEffect(() => {
    if (!url) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    api
      .get(url)
      .then((res) => {
        if (cancelled) return;
        const payload =
          res.data?.data !== undefined ? res.data.data : res.data;
        setState({ data: payload as T, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg =
          status === 404
            ? 'not-available'
            : status === 401
              ? 'unauthorized'
              : (err?.message ?? 'error');
        setState({ data: null, loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return state;
}
